



























































































































































import Vue from "vue";
import { Location } from "vue-router";
import {
  LOGGER_EVENTS,
  ROUTES,
  VUEX_ASSIGNMENT_ACTION_NAMES,
  VUEX_ASSIGNMENT_NAMESPACE,
  VUEX_ROOT_ACTION_NAMES,
  VUEX_ROOT_GETTER_NAMES
} from "@tt-app/constants";
import {
  ApiExerciseBtnAction,
  DrillState,
  ExerciseViewData,
  ExerciseViewMethods,
  DrillAnswer,
  DrillQuestionAndAnswers,
  ExerciseType,
  DrillFinalScore
} from "@tt-app/models/exercise/";
import {
  drillHandler,
  exerciseHandler,
  exerciseNavHandler,
  getNewExerciseViewState,
  getExerciseTypeFromRoute,
  FlatExerciseListDetails,
  nextExerciseRouter
} from "@tt-app/utils/exercise";
import { getAuthInfo, logger } from "@tt-app/utils";
import { LoggerEventDetails } from "@tt-app/models";
import { AudioDecoderPlayer } from "@joyz/app-core/utils/audio";

import AssignmentCompletionModal from "@tt-app/components/assignment/AssignmentCompletionModal.vue";
import DrillBrowser from "@tt-app/components/exercise/DrillBrowser.vue";
import DrillContainer from "@tt-app/components/exercise/DrillContainer.vue";
import DrillLearnedBadgeToast from "@tt-app/components/exercise/DrillLearnedBadgeToast.vue";
import DrillProgress from "@tt-app/components/exercise/DrillProgress.vue";
import DrillQuestionListening from "@tt-app/components/exercise/DrillQuestionListening.vue";
import DrillQuestionPronunciation from "@tt-app/components/exercise/DrillQuestionPronunciation.vue";
import DrillQuestionVocabulary from "@tt-app/components/exercise/DrillQuestionVocabulary.vue";
import DrillReviewSummary from "@tt-app/components/exercise/DrillReviewSummary.vue";
import DrillReviewPronunciation from "@tt-app/components/exercise/DrillReviewPronunciation.vue";
import ExerciseHeaderTitle from "@tt-app/components/exercise/ExerciseHeaderTitle.vue";
import ExerciseTrophy from "@tt-app/components/exercise/common/ExerciseTrophy.vue";
import FeedbackPopup from "@tt-app/components/feedback/FeedbackPopup.vue";
import LayoutExercise from "@tt-app/layouts/LayoutExercise.vue";
import ExerciseScope from "@tt-app/components/exercise/ExerciseScope.vue";
import ExerciseScopeButtons from "@tt-app/components/exercise/ExerciseScopeButtons.vue";
import PracticeModeToast from "@tt-app/components/exercise/common/PracticeModeToast.vue";
import { api } from "@tt-app/api";
import { shuffleArray } from "@tt-app/utils/shuffleArray";
import {
  LimitedModeDetails,
  Session,
  TerratalkLiveGetterNames,
  TerratalkLiveNamespace
} from "@tt-app/store/terratalkLive/types";
import { audioContext } from "@tt-app/utils/audio/audioContext";

const { DETAILS_REQUEST } = VUEX_ASSIGNMENT_ACTION_NAMES;
const { FETCH_DETAILS } = VUEX_ROOT_ACTION_NAMES;
const { FLAT_EXERCISE_LIST } = VUEX_ROOT_GETTER_NAMES;

interface Data extends ExerciseViewData {
  attemptCount: number;
  audioDecoderPlayer: AudioDecoderPlayer;
  drill: DrillState;
  exerciseScopeVisible: boolean;
  feedbackFormVisible: boolean;
  loading: boolean;
  selectedUuids: string[];
  showPracticeModeToast: boolean;
}

interface Methods extends ExerciseViewMethods {
  displayPracticeModeToast: () => void;
  fetchAssignmentDetails: () => void;
  fetchDetails: () => void;
  goToPage: (pageIndex: number) => Promise<void>;
  handleAudioPlaybackError: (
    loggerEvent: LoggerEventDetails,
    playbackError: { occurred: boolean }
  ) => (error: any) => void;
  handleExampleAudioPlayback: (details: { audioUrl: string }) => void;
  handleFeedbackClose: () => void;
  handleRetryAsPractice: () => void;
  handleRetryAsTest: () => void;
  handleStartPracticeClick: () => void;
  handleStartTestClick: () => void;
  handleUserAudioPlayback: (details: { audioUrl: string }) => void;
  initDrill: (options?: { retry: boolean }) => Promise<void>;
  onActionBtnClick: (action: ApiExerciseBtnAction) => void;
  onUserAnswer: (answer: DrillAnswer) => Promise<void>;
  onLoggerEvent: (event: LoggerEventDetails, additionalDetails?: Record<string, any>) => void;
  onPronunciationRecordingStart: () => void;
  refreshAudioPlayer: () => void;
  registerExercisePractice: (
    { problemUuids }?: { problemUuids: string[] },
    tryAgain?: boolean
  ) => void;
  updateFooter: () => void;
  updateSelectedUuids: (uuids: string[]) => void;
}

interface Computed {
  assignmentsCompleted: number;
  assignmentId?: string;
  currentDrillExercise: DrillQuestionAndAnswers | undefined;
  courseUuid: string;
  currentExerciseListIndex: number | null;
  drillType: ExerciseType;
  exerciseUuid: string;
  feedbackRouteParams: {
    withFeedback: string;
    logId: string;
  };
  flatExerciseList: FlatExerciseListDetails[];
  limitedModeActive: boolean;
  limitedModeDetails: LimitedModeDetails;
  logDetails: {
    courseUuid?: string;
    exerciseType?: number;
    exerciseUuid?: string;
    logId?: number;
    uuid?: string;
  };
  nextExercise?: FlatExerciseListDetails;
  nextExerciseButtonText: string;
  session: Session;
  scope: any;
  showFeedbackForm: boolean;
}

/**
 * !! WARNING !!
 * - Unlike Chat based exercises, this views does not control exercise.status
 *   as this value has currently no added value
 */
export default Vue.extend<Data, Methods, Computed, {}>({
  components: {
    ExerciseScopeButtons,
    ExerciseScope,
    ExerciseTrophy,
    AssignmentCompletionModal,
    FeedbackPopup,
    DrillBrowser,
    DrillContainer,
    DrillLearnedBadgeToast,
    DrillProgress,
    DrillQuestionListening,
    DrillQuestionPronunciation,
    DrillQuestionVocabulary,
    DrillReviewSummary,
    DrillReviewPronunciation,
    ExerciseHeaderTitle,
    LayoutExercise,
    PracticeModeToast
  },

  data() {
    const exerciseType: ExerciseType = getExerciseTypeFromRoute(this);

    return {
      ...getNewExerciseViewState(exerciseType, {
        name: ROUTES.COURSE.DETAILS.NAME,
        params: { courseUuid: this.$route.params.courseUuid }
      }),
      attemptCount: 1,
      audioDecoderPlayer: new AudioDecoderPlayer({ audioContext }),
      drill: drillHandler.getNewState(),
      exerciseScopeVisible: true,
      feedbackFormVisible: true,
      loading: false,
      selectedUuids: [],
      showPracticeModeToast: false
    };
  },

  computed: {
    assignmentId() {
      return this.$route.params.assignmentId;
    },
    assignmentsCompleted() {
      const completedCount = this.exercise?.results?.trophyInfo?.numAssignmentsSubmitted;
      return completedCount || 0;
    },
    courseUuid() {
      return this.$route.params.courseUuid;
    },
    currentExerciseListIndex() {
      const listIndex = this.flatExerciseList.findIndex(
        (exerciseDetails: FlatExerciseListDetails) => {
          return exerciseDetails.uuid === this.exerciseUuid;
        }
      );

      return listIndex >= 0 ? listIndex : null;
    },
    exerciseUuid() {
      return this.$route.params.exerciseUuid;
    },
    currentDrillExercise() {
      if (!this.drill.data) return undefined;
      if (this.drill.currentIndex < 0 || this.drill.currentIndex >= this.drill.exercises.length)
        return undefined;

      return this.drill.exercises[this.drill.currentIndex];
    },
    drillType() {
      return getExerciseTypeFromRoute(this);
    },
    feedbackRouteParams() {
      return {
        withFeedback: Boolean(this.exercise.status !== "finished").toString(),
        logId: this.exercise.logId.toString()
      };
    },
    flatExerciseList() {
      return this.$store.getters[FLAT_EXERCISE_LIST]({
        assignmentId: this.assignmentId,
        courseUuid: this.courseUuid
      });
    },
    limitedModeActive() {
      return Boolean(this.limitedModeDetails);
    },
    limitedModeDetails() {
      return this.$store.getters[
        `${TerratalkLiveNamespace}/${TerratalkLiveGetterNames.LIMITED_MODE_DETAILS}`
      ];
    },
    logDetails() {
      return {
        courseUuid: this.drill.data?.course.uuid,
        exerciseType: this.drillType,
        exerciseUuid: this.drill.data?.exercise.uuid,
        logId: this.exercise.logId,
        uuid: this.currentDrillExercise?.question.uuid
      };
    },
    nextExercise() {
      if (this.currentExerciseListIndex === null) return undefined;
      return this.flatExerciseList[this.currentExerciseListIndex + 1];
    },
    nextExerciseButtonText() {
      let i18nString = "exercise.end";

      if (this.nextExercise) {
        i18nString = "exercise.next";
      }

      return this.$__(i18nString);
    },
    scope() {
      return this.drill.data?.exercise.scope;
    },
    session() {
      return this.$store.getters[
        `${TerratalkLiveNamespace}/${TerratalkLiveGetterNames.ACTIVE_SESSION}`
      ];
    },
    showFeedbackForm() {
      const feedbackFormVisible = this.feedbackFormVisible;
      const drillFinished = this.exercise.status === "finished";
      const displayTrophy = this.drill.display === "trophy";

      return feedbackFormVisible && drillFinished && !displayTrophy;
    }
  },

  watch: {
    "nav.requireExitConfirmation": {
      handler(newValue) {
        window.onbeforeunload = null;

        if (newValue) {
          window.onbeforeunload = () => {
            this.onLoggerEvent(LOGGER_EVENTS.USER.TT4W_CLOSE.OTHER);
            return true;
          };
        }
      }
    },
    "drill.display": {
      async handler(newVal: string, oldVal: string) {
        if (newVal !== oldVal) {
          if (newVal === "trophy") {
            // Can exit at any time
            this.nav.requireExitConfirmation = false;

            this.exercise.footer.disabled = true;
            this.updateFooter();

            const finalScore: DrillFinalScore = {
              // first answer is considered correct
              correctCount: this.drill.exercises.filter(
                e => e.allAnswers.length > 0 && e.allAnswers[0].correction.isCorrect
              ).length,
              incorrectCount: this.drill.exercises.filter(
                e => e.allAnswers.length > 0 && !e.allAnswers[0].correction.isCorrect
              ).length,
              skippedCount: this.drill.exercises.filter(e => e.allAnswers.length === 0).length
            };
            this.exercise = await exerciseHandler.endExercise(this, this.exercise, finalScore);

            this.exercise.footer.disabled = false;
            this.updateFooter();
          }
        }
      }
    }
  },

  async created() {
    this.fetchDetails();
    await this.initDrill();
    document.addEventListener("visibilitychange", this.refreshAudioPlayer);
  },

  async mounted() {
    if (this.limitedModeActive) {
      this.exerciseScopeVisible = false;
    }
  },

  beforeDestroy() {
    this.audioDecoderPlayer.destroy();
    document.removeEventListener("visibilitychange", this.refreshAudioPlayer);
  },

  beforeRouteEnter(to, from, next) {
    next((vm: Vue) => {
      vm.$data.exercise = exerciseHandler.checkAssignmentId(vm.$data.exercise, from);
    });
  },

  // https://router.vuejs.org/guide/advanced/navigation-guards.html#in-component-guards
  async beforeRouteLeave(to, from, next) {
    exerciseNavHandler.beforeRouteLeave(this.nav, state => (this.nav = state), to, from, next);
  },

  methods: {
    displayPracticeModeToast() {
      this.showPracticeModeToast = true;

      window.setTimeout(() => {
        this.showPracticeModeToast = false;
      }, 5000);
    },

    exit() {
      this.onLoggerEvent(LOGGER_EVENTS.USER.EXERCISE_CLOSE.SUCCESS);
      exerciseNavHandler.exit(this, this.nav, state => (this.nav = state), {
        feedbackParams: this.feedbackRouteParams
      });
    },

    async fetchAssignmentDetails() {
      await this.$store.dispatch(`${VUEX_ASSIGNMENT_NAMESPACE}/${DETAILS_REQUEST}`, {
        assignmentId: this.$route.params.assignmentId
      });
    },

    fetchDetails() {
      this.$store.dispatch(`${FETCH_DETAILS}`, {
        assignmentId: this.assignmentId,
        courseUuid: this.courseUuid
      });
    },

    handleAudioPlaybackError(loggerEvent, playbackError) {
      return customError => {
        playbackError.occurred = true;
        this.onLoggerEvent(loggerEvent, {
          error: customError.error,
          url: customError.audioUrl
        });
      };
    },

    async handleExampleAudioPlayback({ audioUrl }) {
      this.onLoggerEvent(LOGGER_EVENTS.USER.EXAMPLE_AUDIO_PLAY_PRESS.SUCCESS, {
        url: audioUrl
      });

      if (this.audioDecoderPlayer.status === "playing") {
        this.audioDecoderPlayer.stop();
      }

      const playbackError = { occurred: false };
      await audioContext.resume();
      await this.audioDecoderPlayer.play(audioUrl, {
        onError: this.handleAudioPlaybackError(
          LOGGER_EVENTS.APPLICATION.EXAMPLE_AUDIO_PLAY.FAILURE,
          playbackError
        )
      });

      if (!playbackError.occurred) {
        this.onLoggerEvent(LOGGER_EVENTS.APPLICATION.EXAMPLE_AUDIO_PLAY.SUCCESS, {
          url: audioUrl
        });
      }
    },

    handleFeedbackClose() {
      this.feedbackFormVisible = false;
    },

    async handleRetryAsPractice() {
      const incorrectQuestions = this.drill.exercises.filter(question => {
        return !question.lastAnswer || !question.lastAnswer?.correction.isCorrect;
      });

      const incorrectQuestionUuids = incorrectQuestions.map(incorrectQuestion => {
        return incorrectQuestion.question.uuid;
      });

      await this.initDrill({ retry: true });

      const practiceData = this.drill.data?.exercise.pages.filter(page => {
        const question: any = page.body[0];

        const drillQuestion = [
          "DRILL_VOCABULARY",
          "DRILL_LISTENING",
          "DRILL_PRONUNCIATION"
        ].includes(question.type);

        if (!drillQuestion) return true;

        return incorrectQuestionUuids.includes(question.uuid);
      });

      const questions = this.drill.exercises.filter(question => {
        return incorrectQuestionUuids.includes(question.question.uuid);
      });

      this.drill.exercises = questions;
      if (this.drill.data) {
        this.drill.data.exercise.practicePages = practiceData;
      }

      await this.registerExercisePractice({ problemUuids: incorrectQuestionUuids }, true);
      this.displayPracticeModeToast();
      this.exerciseScopeVisible = false;
      this.drill.display = "drill-question";
      this.drill.practiceMode = true;
      this.updateFooter();
    },

    async handleRetryAsTest() {
      await this.initDrill();
      this.handleStartTestClick();
    },

    async handleStartPracticeClick() {
      const practiceData = this.drill.data?.exercise.pages.filter(page => {
        const question: any = page.body[0];

        const drillQuestion = [
          "DRILL_VOCABULARY",
          "DRILL_LISTENING",
          "DRILL_PRONUNCIATION"
        ].includes(question.type);

        if (!drillQuestion) return true;

        return this.selectedUuids.includes(question.uuid);
      });

      const questions = this.drill.exercises.filter(question => {
        return this.selectedUuids.includes(question.question.uuid);
      });

      this.drill.exercises = questions;
      if (this.drill.data) {
        this.drill.data.exercise.practicePages = practiceData;
      }

      await this.registerExercisePractice({ problemUuids: this.selectedUuids });
      this.displayPracticeModeToast();
      this.exerciseScopeVisible = false;
      this.drill.display = "drill-question";
      this.drill.practiceMode = true;
      this.updateFooter();
    },

    handleStartTestClick() {
      const shuffledExercises = shuffleArray(this.drill.exercises);

      this.drill.exercises = shuffledExercises;

      this.exerciseScopeVisible = false;
      this.drill.display = "drill-question";
      this.drill.practiceMode = false;
      this.updateFooter();
    },

    async handleUserAudioPlayback({ audioUrl }) {
      this.onLoggerEvent(LOGGER_EVENTS.USER.USER_AUDIO_PLAY_PRESS.SUCCESS);

      if (this.audioDecoderPlayer.status === "playing") {
        this.audioDecoderPlayer.stop();
      }

      const playbackError = { occurred: false };
      await audioContext.resume();
      await this.audioDecoderPlayer.play(audioUrl, {
        onError: this.handleAudioPlaybackError(
          LOGGER_EVENTS.APPLICATION.USER_AUDIO_PLAY.FAILURE,
          playbackError
        )
      });

      if (!playbackError.occurred) {
        this.onLoggerEvent(LOGGER_EVENTS.APPLICATION.USER_AUDIO_PLAY.SUCCESS);
      }
    },

    onExitCancelled() {
      this.onLoggerEvent(LOGGER_EVENTS.USER.EXERCISE_CLOSE_CANCELLATION.SUCCESS);
      this.nav = exerciseNavHandler.onExitCancelled(this.nav);
    },

    onExitConfirmed() {
      this.onLoggerEvent(LOGGER_EVENTS.USER.EXERCISE_CLOSE_CONFIRMATION.SUCCESS);
      exerciseNavHandler.onExitConfirm(this, this.nav, state => (this.nav = state), {
        feedbackParams: this.feedbackRouteParams
      });
    },

    async onLoggerEvent(event, additionalDetails) {
      logger.logEvent(event, { ...this.logDetails, ...additionalDetails });
    },

    async goToPage(pageIndex) {
      this.onLoggerEvent(LOGGER_EVENTS.USER.EXERCISE_REVIEW_CHANGE_PAGE.SUCCESS);
      this.drill = drillHandler.moveToPage(this.drill, pageIndex);
      await this.$nextTick();
    },

    async initDrill(options) {
      // (Re-)init states
      let fallback: Location;
      if (this.assignmentId) {
        await this.fetchAssignmentDetails();
      }

      if (this.$route.params.assignmentId && this.$store.state.assignment.details) {
        fallback = {
          name: ROUTES.ASSIGNMENT.DETAILS.NAME,
          params: {
            assignmentId: this.$route.params.assignmentId
          }
        };
      } else {
        fallback = {
          name: ROUTES.COURSE.DETAILS.NAME,
          params: {
            courseUuid: this.$route.params.courseUuid
          }
        };
      }

      await this.$nextTick();

      const newState = getNewExerciseViewState(this.drillType, fallback);
      this.exercise = newState.exercise;
      this.nav = newState.nav;
      this.nav.requireExitConfirmation = true; // Drill requires confirmation right away

      this.drill = drillHandler.getNewState();

      // Load drills
      this.exercise.status = "loading";
      if (Number(this.assignmentId)) {
        this.exercise.assignmentId = Number(this.assignmentId);
      }
      try {
        this.drill = await drillHandler.loadData(this, this.drill, this.exercise, true);
      } catch (err) {
        this.loadingError = this.$__("drill.error.loading");
        this.exercise.status = "error";
        return;
      }
      this.exercise.status = "loaded";

      // Update states after data loading
      this.exercise = exerciseHandler.loadHeaderFromDrill(this.exercise, this.drill);
      this.updateFooter();
      this.exercise.footer.disabled = true;

      // Start
      if (!options?.retry) {
        this.exercise = await exerciseHandler.startExerciseDrill(this, this.exercise, this.drill);
      }
      this.exercise.status = "in-progress";
      this.exercise.footer.disabled = false;
      this.feedbackFormVisible = true;
    },

    async onActionBtnClick(action) {
      // Start action
      if (action === "NEXT") {
        this.onLoggerEvent(LOGGER_EVENTS.USER.EXERCISE_NEXT.SUCCESS);
        // https://stackoverflow.com/a/46534088/4906586

        this.drill = drillHandler.moveToPage(this.drill);
        this.updateFooter();
        this.attemptCount = 1;
      } else if (action === "SKIP") {
        this.onLoggerEvent(LOGGER_EVENTS.USER.EXERCISE_SKIP.SUCCESS);

        this.exercise.footer.disabled = true;
        this.loading = true;
        this.drill = await drillHandler.onUserSkipped(this, this.exercise, this.drill);
        this.updateFooter();

        await this.$nextTick();
        this.loading = false;
        this.exercise.footer.disabled = false;
      }
      // Motivated user wants to start again
      else if (action === "TRY_AGAIN") {
        if (
          !this.session &&
          (this.exercise.results?.finalScore.incorrectCount ||
            this.exercise.results?.finalScore.skippedCount)
        ) {
          this.handleRetryAsPractice();
        } else {
          this.handleRetryAsTest();
        }
      }
      // Non-motivated user wants to get out here as soon as possible
      else if (action === "NEXT_EXERCISE") {
        this.onLoggerEvent(LOGGER_EVENTS.USER.EXERCISE_FINISH.SUCCESS);
        nextExerciseRouter({
          assignmentId: this.assignmentId,
          courseUuid: this.courseUuid,
          ...this.nextExercise
        });
      }
    },

    async onUserAnswer(answer) {
      this.loading = true;
      this.exercise.footer.disabled = true;

      if (answer) {
        this.drill = await drillHandler.onUserSubmit(this, this.exercise, this.drill, answer);
      }
      this.updateFooter();

      await this.$nextTick();
      this.loading = false;
      this.exercise.footer.disabled = false;
    },

    onPronunciationRecordingStart() {
      this.exercise.footer.disabled = true;

      if (this.drill.exercises[this.drill.currentIndex].allAnswers.length > 0) {
        this.attemptCount = this.drill.exercises[this.drill.currentIndex].allAnswers.length + 1;
      }
    },

    refreshAudioPlayer() {
      if (document.visibilityState === "visible") {
        console.log("refreshing audio player");
        this.audioDecoderPlayer = new AudioDecoderPlayer({ audioContext });
      }
    },

    async registerExercisePractice(problemUuids, tryAgain) {
      const authInfo = await getAuthInfo(this.$store.getters);
      const details = {
        courseUuid: this.$route.params.courseUuid,
        exerciseUuid: this.$route.params.exerciseUuid,
        exerciseType: this.exercise.exerciseType,
        logId: tryAgain ? undefined : this.exercise.logId,
        ...problemUuids
      };

      const apiPayload = { ...authInfo, ...details };

      const response: any = await api.exercise.startExercisePractice(apiPayload);

      this.selectedUuids = problemUuids ? problemUuids.problemUuids : [];

      this.exercise.logId = response.logId;
      this.nav.requireExitConfirmation = true;
    },

    updateFooter() {
      this.exercise = exerciseHandler.loadFooterFromDrill(this, this.exercise, this.drill);
    },

    updateSelectedUuids(uuids) {
      this.selectedUuids = uuids;
    }
  }
});
